/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useEffect} from 'react'
import {useIntl} from 'react-intl'
import useNavigation from '../../hooks/use-navigation'
import useEffectEvent from '../../hooks/use-effect-event'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {
    ShopperBasketsMutations,
    useCustomerBaskets,
    useCustomerId,
    useCustomerType,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {usePrevious} from '@chakra-ui/react'
import {isServer} from '../../utils/utils'
import {API_ERROR_MESSAGE, CART_HREF} from '../../constants'

/**
 * Joins basket and customer hooks into a single hook for initializing their states
 * when the app loads on the client-side. Should only be use at top-level of app.
 * @returns {Object} - customer and basket objects
 */
const useShopper = ({currency} = {}) => {
    // Update basket with currency and customer email
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const navigate = useNavigation()
    const {formatMessage} = useIntl()

    const updateBasket = useShopperBasketsMutation(ShopperBasketsMutations.UpdateBasket)
    const updateCustomerForBasket = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateCustomerForBasket
    )

    const onBasketCurrencyDiffer = useEffectEvent(() => {
        updateBasket.mutate({
            parameters: {basketId: basket.basketId},
            body: {currency}
        })
    })
    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            onBasketCurrencyDiffer()
        }
    }, [basket?.currency, currency, onBasketCurrencyDiffer])

    const onBasketCustomerDiffer = useEffectEvent(() => {
        updateCustomerForBasket.mutate({
            parameters: {basketId: basket.basketId},
            body: {
                email: customer.email
            }
        })
    })
    useEffect(() => {
        // update the basket customer email
        if (
            basket?.basketId &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            onBasketCustomerDiffer()
        }
    }, [
        basket?.basketId,
        customer?.isRegistered,
        customer?.email,
        basket?.customerInfo?.email,
        onBasketCustomerDiffer
    ])

    // TODO v3: do we need to create basket here - according to ref app that happens when first item is added to basket
    // // Handle basket init/updates in response to customer/basket changes.
    // const onCustomerBasketChange = useEffectEvent((hasBasket) => {
    //     // We have a customer but no basket, so we fetch a new or existing basket
    //     if (customer.isInitialized && !hasBasket) {
    //         basket.getOrCreateBasket().catch(setAsyncError)
    //         return
    //     }
    //
    //     // We have a customer and a basket, but the basket does not belong to this customer
    //     // so we get their existing basket or create a new one for them
    //     if (
    //         hasBasket &&
    //         customer.isInitialized &&
    //         customer.customerId !== basket.customerInfo.customerId
    //     ) {
    //         basket.getOrCreateBasket().catch(setAsyncError)
    //         return
    //     }
    //
    //     // We have a registered customer (customer with email), and we have their basket,
    //     // but the email applied to the basket is missing or doesn't match the customer
    //     // email. In this case, we update the basket with their email.
    //     if (
    //         hasBasket &&
    //         customer.isRegistered &&
    //         customer.customerId === basket.customerInfo.customerId &&
    //         customer.email !== basket.customerInfo.email
    //     ) {
    //         basket.updateCustomerInfo({email: customer.email}).catch(setAsyncError)
    //         return
    //     }
    // })
    // useEffect(() => {
    //     onCustomerBasketChange(basket.loaded, customer.authType)
    // }, [customer.authType, basket.loaded, onCustomerBasketChange])

    // TODO v3: pwa 3 moved the merge basket to the login action
    // Merge basket when registered customer logs in
    const {customerType} = useCustomerType()
    const customerId = useCustomerId()
    const prevAuthType = usePrevious(customerType)
    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const onUserLogin = useEffectEvent(async () => {
        const hasBasket = baskets?.baskets?.length > 0
        // we only want to merge basket when the user is logged in as a recurring user
        // only recurring users trigger the login mutation, new user triggers register mutation
        // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
        // if you change logic here, also change it in login page
        const shouldMergeBasket = hasBasket && prevAuthType === 'guest'
        if (shouldMergeBasket) {
            const mergedBasket = await mergeBasket.mutateAsync(
                {
                    headers: {
                        // This is not required since the request has no body
                        // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                        'Content-Type': 'application/json'
                    },
                    parameters: {
                        productItemMergeMode: 'sum_quantities',
                        createDestinationBasket: true
                    }
                },
                {
                    onError: () => {
                        // go to cart page because the merge with guest basket cannot be done
                        console.error('basket merge error')
                        navigate(CART_HREF, 'push', {
                            cartError: formatMessage(API_ERROR_MESSAGE)
                        })
                    }
                }
            )
            // merge basket does not trigger SFCC hooks and that's why
            // custom fields like PLI promotion prices are not set
            // therefore we have to explicitly trigger GET, PUT or PATCH call
            updateBasket.mutate({
                parameters: {basketId: mergedBasket.basketId},
                body: {currency: mergedBasket.currency}
            })
        }
    })
    useEffect(() => {
        if (customerType === 'registered') {
            onUserLogin()
        }
    }, [customerType, onUserLogin])
}

export default useShopper
